"use client";

import Link from "next/link";
import CodeMirror from "@uiw/react-codemirror";
import { githubDark } from "@uiw/codemirror-theme-github";
import TrySeozastCTA from "@/app/components/TrySeozastCTA";
import { javascript } from "@codemirror/lang-javascript";
import Head from "next/head";

const Page = () => {
    const canonicalUrl = 'https://seozast.com/articles/seo-vsu-pseo-vs-geo-2025';

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Head con canonical */}
            <Head>
                <title>AI SEO in 2025: How to Make Your Content AI-Citation Ready</title>
                <link rel="canonical" href={canonicalUrl} />
            </Head>
            <section id="intro" className="mb-8">
                <h1 className="text-3xl font-bold mb-4">What is PSEO and SEO, and How They Improve Your App</h1>
                <div className="bg-neutral-100 rounded-xl p-6 mb-8 border border-neutral-200">
                    <h2 className="text-xl font-semibold mb-2">At a Glance</h2>
                    <p className="text-neutral-700">
                        SEO helps your app rank on Google. PSEO lets you scale it. In 2025, combining both ensures visibility across traditional and AI-powered search tools like ChatGPT, Perplexity, and Google's SGE. Learn how to implement both in your Next.js project.
                    </p>
                </div>
                <p className="text-md mb-4">
                    In the competitive world of web applications, visibility is everything. Search Engine Optimization (SEO), Programmatic SEO (PSEO), and Generative Engine Optimization (GEO) are powerful strategies to boost your app’s discoverability. This guide explains what SEO, PSEO, and GEO are, why they matter for your Next.js app, and how to implement them to improve performance, search visibility, and AI indexing.
                </p>
            </section>

            <section id="what-is-seo" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">What is SEO?</h2>
                <p className="text-md mb-4">
                    SEO is the process of optimizing your web app to rank higher on search engine results pages (SERPs) like Google, Bing, or Yahoo. By improving your app’s content, structure, and technical setup, you make it easier for search engines to crawl, index, and rank your pages. Traditional SEO includes:
                </p>
                <ul className="list-disc pl-6 text-md mb-4">
                    <li><strong>Keyword Optimization</strong>: Using relevant keywords (e.g., “Next.js SEO”) in titles, headings, and content.</li>
                    <li><strong>Metadata</strong>: Crafting compelling title tags and meta descriptions to improve click-through rates.</li>
                    <li><strong>Site Structure</strong>: Ensuring fast load times, mobile-friendliness, and clean URLs.</li>
                    <li><strong>Backlinks</strong>: Earning links from reputable sites to boost authority.</li>
                    <li><strong>Content Quality</strong>: Publishing valuable, user-focused content.</li>
                </ul>
            </section>

            <section id="what-is-pseo" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">What is PSEO?</h2>
                <p className="text-md mb-4">
                    Programmatic SEO (PSEO) automates SEO content creation using data and templates to scale. For example, a travel app might generate thousands of pages like “Best hotels in [city]” from a database. PSEO is ideal for apps with large datasets or repetitive content structures.
                </p>
                <p className="text-md mb-4">
                    PSEO leverages tools like dynamic routes and Markdown files, as shown in our <Link href="/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo" className="text-emerald-500 hover:underline">Markdown guide</Link>. It saves time, targets long-tail keywords, and increases visibility.
                </p>
            </section>

            <section id="comparison" className="overflow-x-auto my-6">
                <table className="w-full text-sm text-left border border-neutral-200">
                    <thead className="bg-neutral-100">
                        <tr>
                            <th className="px-4 py-2 border">Aspect</th>
                            <th className="px-4 py-2 border">SEO</th>
                            <th className="px-4 py-2 border">PSEO</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="px-4 py-2 border">Effort</td>
                            <td className="px-4 py-2 border">Manual</td>
                            <td className="px-4 py-2 border">Automated</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 border">Scalability</td>
                            <td className="px-4 py-2 border">Limited</td>
                            <td className="px-4 py-2 border">High</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 border">GEO-friendly</td>
                            <td className="px-4 py-2 border">Only if structured</td>
                            <td className="px-4 py-2 border">Yes, ideal</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section id="how-they-help" className="my-8">
                <h2 className="text-2xl font-semibold mb-4">How SEO, PSEO, and GEO Help Your App</h2>
                <ul className="list-disc pl-6 text-md mb-4">
                    <li><strong>SEO:</strong> Improves ranking and click-through on search engines.</li>
                    <li><strong>PSEO:</strong> Scales your content to dominate long-tail keywords.</li>
                    <li><strong>GEO:</strong> Optimizes your content for generative AI results (SGE, ChatGPT, etc.).</li>
                </ul>
                <p className="text-md mb-4">
                    According to <a href="https://www.forbes.com/advisor/business/ai-statistics/" target="_blank" className="text-emerald-500 hover:underline">Forbes</a>, over 50% of users now rely on AI tools to find answers. GEO ensures your structured content is visible to these tools.
                </p>
            </section>

            <section id="seo-example" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">SEO Example: Dynamic Metadata</h2>
                <CodeMirror
                    value={`import { Metadata } from 'next';
import { getArticleBySlug } from '@/lib/articles';

export async function generateMetadata({ params }) {
  const article = getArticleBySlug(params.slug);
  return {
    title: article?.metadata.title || article.slug,
    description: article?.metadata.description,
    keywords: article?.metadata.tags?.join(', '),
    openGraph: {
      title: article?.metadata.title,
      description: article?.metadata.description,
      url: \`https://your-domain.com/articles/\${article.slug}\`,
    },
  };
}`}
                    extensions={[javascript()]}
                    theme={githubDark}
                    height="300px"
                />
            </section>

            <section id="pseo-example" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">PSEO Example: City Pages</h2>
                <CodeMirror
                    value={`export async function generateStaticParams() {
  const cities = ['new-york', 'london', 'tokyo'];
  return cities.map((city) => ({ city }));
}

export async function generateMetadata({ params }) {
  return {
    title: \`Best Restaurants in \${params.city}\`,
    description: \`Discover dining in \${params.city}.\`,
  };
}

export default function CityPage({ params }) {
  return (
    <div>
      <h1>Best Restaurants in {params.city}</h1>
      <p>Explore top spots in {params.city}.</p>
    </div>
  );
}`}
                    extensions={[javascript()]}
                    theme={githubDark}
                    height="300px"
                />
            </section>

            <section id="faq" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">FAQs</h2>
                <ul className="list-disc pl-6 text-md space-y-2">
                    <li>SEO improves ranking and click-through via traditional search.</li>
                    <li>PSEO scales SEO using templates and data.</li>
                    <li>GEO helps content surface in AI-driven platforms.</li>
                </ul>
            </section>

            <section id="key-takeaways" className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">Key Takeaways</h2>
                <ul className="list-disc pl-6 text-md space-y-2">
                    <li>Combine SEO, PSEO, and GEO for full visibility in 2025.</li>
                    <li>Structure content for humans and machines (search & AI).</li>
                    <li>Use dynamic sitemaps, metadata, and Markdown.</li>
                </ul>
            </section>

            <TrySeozastCTA />

            <div className="mt-16">
                <h2 className="text-2xl font-semibold mb-6 text-neutral-900">Related Articles</h2>
                <ul className="space-y-4 text-neutral-700 list-disc pl-6">
                    <li>
                        <a
                            href="/articles/how-to-submit-urls-to-bing-with-indexnow"
                            className="text-emerald-600 hover:underline font-medium"
                        >
                            How to Submit URLs to Bing with IndexNow (and Why It Matters)
                        </a>
                    </li>
                    <li>
                        <a
                            href="/articles/get-found-in-chatgpt-and-perplexity"
                            className="text-emerald-600 hover:underline font-medium"
                        >
                            Get Found in ChatGPT and Perplexity
                        </a>
                    </li>
                    <li>
                        <a
                            href="/articles/geo-vs-seo"
                            className="text-emerald-600 hover:underline font-medium"
                        >
                            GEO vs. SEO: Mastering Search in the AI Era of 2025
                        </a>
                    </li>
                </ul>
            </div>

        </div>
    );
};

export default Page;